// @ts-strict-ignore
import { isNil, isNumber } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { PixelRatio } from 'react-native';
import { View } from '@shared/components/View';
import { MBCircularProgressBar, UnvalidatedStyles } from '@shared/components/loading';
import { useImageSrcAndDimensions } from '@shared/util/hooks';
import { MBChatAttachmentProps } from '../index';

// the smallest dimensions to render the image bubble
// sized to the loading spinner with some pad.
const MIN_IMAGE_SIZE = UnvalidatedStyles.circularProgressBarSize.normal + 20;

// Module-level cache: keyed by attachment.fileKey so that displayedSrc survives
// component remounts (which happen when a message moves from cachedMessages to
// the confirmed Apollo messages list). Without this, on remount the component
// initialises displayedSrc straight to the imgix URL and shows blank while the
// CDN fetches it instead of continuing to show the already-loaded blob URL.
const displayedSrcByFileKey = new Map<string, string>();

// the tallest to allow an image to render, in vh
const MAX_VIEWPORT_HEIGHT_PCT = 60;

// Calculate the maximum dims we'll request from imgix for attachment bubbles.
// Use 75% of the screen width, but never more than 1200.
const SCREEN_WIDTH = window.innerWidth;
const MAX_SCREEN_DIM = SCREEN_WIDTH * 0.75;
const MAX_BUBBLE_DIM = 1200;
const MAX_DIM = Math.min(MAX_SCREEN_DIM, MAX_BUBBLE_DIM);

/*
    Default image attachment. When pressed, it shows a modal with an image preview.
*/
export function MBChatImageAttachment({
  attachment,
  isSending,
  progress,
  forcedHeight,
  backgroundColor,
  containerWidth,
}: MBChatAttachmentProps) {
  const { src, width, height, ratio, isLandscape, isPortrait } = useImageSrcAndDimensions({
    image: attachment,
    containerWidth: MAX_DIM,
    containerHeight: MAX_DIM,
    size: 'contain',
  });

  // Prevent flash when transitioning from blob URL (during upload) to the
  // final imgix URL. We preload the new src in memory and only commit the
  // switch once the image is fully loaded, so the old image stays visible
  // until the replacement is ready.
  //
  // The lazy initialiser checks the module-level cache first so that even if
  // this component remounts (message moves from cachedMessages → confirmed
  // messages list), we start from the last successfully shown src (the blob
  // URL) rather than jumping straight to the new imgix URL with nothing to
  // show while the CDN fetches it.
  const [displayedSrc, setDisplayedSrc] = useState<string>(() => {
    return displayedSrcByFileKey.get(attachment.fileKey) ?? src;
  });
  const preloadRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!src || src === displayedSrc) {
      return;
    }
    const img = new window.Image();
    preloadRef.current = img;
    const commit = () => {
      if (preloadRef.current === img) {
        displayedSrcByFileKey.set(attachment.fileKey, src);
        setDisplayedSrc(src);
      }
    };
    img.onload = commit;
    // If the new src fails for any reason, switch to it anyway rather than
    // staying frozen on a potentially stale displayedSrc indefinitely.
    img.onerror = commit;
    img.src = src;
    return () => {
      img.onload = null;
      img.onerror = null;
      preloadRef.current = null;
    };
  }, [src, attachment.fileKey, displayedSrc]);

  const dpr = PixelRatio.get();
  // Treat containerWidth=0 as "not yet measured" (MBHover starts at 0 before
  // its onSize fires). Using 0 would collapse maxWidth and maximumHeight to 0,
  // causing the image to render at the minimum 56px before jumping to full
  // size, which throws off the scroll-to-bottom anchor.
  let maxWidth = Math.min(
    MAX_DIM / dpr,
    containerWidth ? containerWidth * 0.75 : window.innerWidth * 0.75,
  );
  const maximumHeight = !isNil(forcedHeight)
    ? forcedHeight
    : Math.min(maxWidth / ratio, window.innerHeight * (MAX_VIEWPORT_HEIGHT_PCT / 100));
  if (!isNil(forcedHeight)) {
    maxWidth = forcedHeight * ratio;
  }

  return (
    <View
      center
      style={{
        width: isLandscape ? width : null,
        height: !isNil(forcedHeight) ? forcedHeight : isPortrait ? height : null,
        minWidth: MIN_IMAGE_SIZE,
        minHeight: MIN_IMAGE_SIZE,
        maxWidth: isLandscape ? maxWidth : null,
        maxHeight: isPortrait ? maximumHeight : null,
        backgroundColor: !isNil(backgroundColor) ? backgroundColor : null,
        flexShrink: 1,
      }}
    >
      {(isSending ?? isNumber(progress)) && (
        <View absolute center>
          <MBCircularProgressBar progress={progress} />
        </View>
      )}
      {!isNil(displayedSrc) && (
        <img
          src={displayedSrc}
          onLoad={() => displayedSrcByFileKey.set(attachment.fileKey, displayedSrc)}
          onError={() => {
            if (src && displayedSrc !== src) {
              setDisplayedSrc(src);
            }
          }}
          style={{
            maxWidth: isLandscape ? '100%' : null,
            maxHeight: isPortrait ? maximumHeight : null,
            width: isLandscape ? width : null,
            height: isPortrait ? height : null,
            opacity: isSending ? 0.5 : 1.0,
          }}
          alt=""
        />
      )}
    </View>
  );
}
